import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MobileFullscreenPage } from '@/components/layout/MobileFullscreenPage';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { pickSelfiePrompt } from '@/lib/idVerification';
import { toast } from '@/lib/toast';
import { Camera, ShieldCheck, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { useIdVerification } from '@/hooks/useIdVerification';
import { STORAGE_BUCKETS } from '@/lib/storageBuckets';

interface VerifyIdSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Slot = 'id' | 'selfie';

function PhotoSlot({
  label,
  helper,
  file,
  onPick,
  onClear,
}: {
  label: string;
  helper: React.ReactNode;
  file: File | null;
  onPick: (f: File) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-3">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium">{label}</span>
        {file && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
          >
            <Trash2 className="h-3 w-3" /> Remove
          </button>
        )}
      </div>
      <div className="text-xs text-muted-foreground mb-3 leading-relaxed">{helper}</div>

      {preview ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="relative w-full aspect-[4/3] rounded-lg overflow-hidden bg-black/40 group"
        >
          <img src={preview} alt={label} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs text-white gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Retake
          </div>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full aspect-[4/3] rounded-lg border-2 border-dashed border-white/15 bg-black/20 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors"
        >
          <Camera className="h-6 w-6" />
          <span className="text-sm">Take photo or upload</span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function Body({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const { invalidate } = useIdVerification();
  const [prompt] = useState(() => pickSelfiePrompt());
  const [idFile, setIdFile] = useState<File | null>(null);
  const [selfieFile, setSelfieFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = !!idFile && !!selfieFile && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !user) return;
    setSubmitting(true);
    try {
      const requestId = crypto.randomUUID();
      const idExt = (idFile.name.split('.').pop() || 'jpg').toLowerCase();
      const selfieExt = (selfieFile.name.split('.').pop() || 'jpg').toLowerCase();
      const idPath = `${user.id}/${requestId}/id.${idExt}`;
      const selfiePath = `${user.id}/${requestId}/selfie.${selfieExt}`;

      const [idRes, selfieRes] = await Promise.all([
        supabase.storage.from(STORAGE_BUCKETS.idVerification).upload(idPath, idFile, { upsert: false, contentType: idFile.type || 'image/jpeg' }),
        supabase.storage.from(STORAGE_BUCKETS.idVerification).upload(selfiePath, selfieFile, { upsert: false, contentType: selfieFile.type || 'image/jpeg' }),
      ]);
      if (idRes.error) throw idRes.error;
      if (selfieRes.error) throw selfieRes.error;

      const { error: insertErr } = await supabase
        .from('id_verification_requests')
        .insert({
          id: requestId,
          user_id: user.id,
          id_photo_path: idPath,
          selfie_photo_path: selfiePath,
          selfie_prompt: prompt,
          status: 'pending',
        });
      if (insertErr) throw insertErr;

      toast.success('Submitted for review', {
        description: 'We usually review within 48 hours.',
      });
      invalidate();
      onClose();
    } catch (e: any) {
      toast.error('Could not submit', { description: e?.message ?? String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground leading-relaxed">
        Get a gold{' '}
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border border-active-gold/40 bg-active-gold/[0.08] text-active-gold text-[10.5px] font-semibold align-middle relative -top-px"
        >
          <ShieldCheck className="h-3 w-3" strokeWidth={2.25} />
          ID Verified
        </span>{' '}
        badge. Verified sellers rank higher and show up in buyers' "Verified only" filter.
        Photos are stored privately and auto-deleted 30 days after review.
      </div>

      <PhotoSlot
        label="1. Government ID (front)"
        helper="Driver's license, passport, or state ID. Make sure your name and photo are readable."
        file={idFile}
        onPick={setIdFile}
        onClear={() => setIdFile(null)}
      />

      <PhotoSlot
        label="2. Selfie with prompt"
        helper={
          <>
            <span className="text-foreground/90 font-medium">{prompt}</span>
            <br />
            Your face must be clearly visible in the same shot.
          </>
        }
        file={selfieFile}
        onPick={setSelfieFile}
        onClear={() => setSelfieFile(null)}
      />

      <Button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="w-full h-12 text-base font-semibold"
      >
        {submitting ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…</>
        ) : (
          'Submit for review'
        )}
      </Button>
    </div>
  );
}

export function VerifyIdSheet({ open, onOpenChange }: VerifyIdSheetProps) {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <MobileFullscreenPage
        open={open}
        onClose={() => onOpenChange(false)}
        title="Verify your ID"
      >
        <div className="px-5 py-5">
          <Body onClose={() => onOpenChange(false)} />
        </div>
      </MobileFullscreenPage>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-background border-white/[0.08]">
        <DialogHeader>
          <DialogTitle>Verify your ID</DialogTitle>
        </DialogHeader>
        <Body onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
